import { useEffect, useRef, useState } from "react";
import { getToken } from "./api";
import type { LiveEvent } from "./types";

// Live build and deploy progress.
//
// EventSource cannot set an Authorization header, and putting a session token
// in a query string would land it in server logs, proxy logs and browser
// history. So the stream is read with fetch + a ReadableStream instead: the
// header works, and the parsing is a dozen lines.
//
// The trade is that reconnection is ours to implement rather than the
// browser's — hence the backoff below.

export function useLiveEvents(onEvent: (ev: LiveEvent) => void) {
  const [connected, setConnected] = useState(false);
  // The callback is held in a ref so a re-render with a new closure does not
  // tear down and re-establish the stream — which, mid-build, would drop log
  // lines on every state update the log itself caused.
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;
    let attempt = 0;

    async function connect() {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch("/api/events", {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`stream failed (${res.status})`);

        setConnected(true);
        attempt = 0;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line. Anything after the last
          // separator is a partial frame and stays in the buffer.
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";

          for (const frame of frames) {
            for (const line of frame.split("\n")) {
              if (!line.startsWith("data:")) continue; // ": ping" heartbeats land here
              try {
                handler.current(JSON.parse(line.slice(5).trim()) as LiveEvent);
              } catch {
                // A malformed frame must not kill the stream.
              }
            }
          }
        }
      } catch {
        // Fall through to the reconnect below.
      }

      setConnected(false);
      if (stopped) return;

      // Exponential backoff, capped. A server that is down does not benefit
      // from being asked every 200ms, and a build in progress is worth
      // reconnecting for.
      attempt = Math.min(attempt + 1, 6);
      const delay = Math.min(1000 * 2 ** (attempt - 1), 30_000);
      setTimeout(() => {
        if (!stopped) void connect();
      }, delay);
    }

    void connect();
    return () => {
      stopped = true;
      controller.abort();
    };
  }, []);

  return connected;
}
